import React from 'react';

import type { IntlShape } from 'react-intl-next';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import type { BaseAIAction } from '@atlassian/ai-smart-button-api';

import type { AIGlobalOptIn } from '../../../types/types';
import type {
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemWithOptions,
} from '../../../ui/prebuilt/config-items/config-items';
import { createOpenAIModalCommand } from '../../decoration/actions';

function convertConfigItemToAction({
	config,
	aiGlobalOptIn,
	intl,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	config: EditorPluginAIConfigItem;
	intl: IntlShape;
	view: EditorView;
}): BaseAIAction {
	return {
		id: config.key.toString(),
		title: intl.formatMessage(config.title),
		description: intl.formatMessage(config.description),
		icon: config.icon?.({ formatMessage: intl.formatMessage }) || (
			<AtlassianIntelligenceIcon label="" spacing="spacious" />
		),
		onClick: () => {
			view.focus();
			createOpenAIModalCommand({
				state: view.state,
				configItem: config,
				lastTriggeredFrom: 'rovoButton',
				aiGlobalOptIn,
				locale: intl.locale,
			})(view.state, view.dispatch);
		},
		closePanelOnSelection: true,
		status: config.statusLozengeType,
	};
}

export const mapConfigItemsToActions =
	({
		intl,
		dynamicSurfaceConfigItems,
		aiGlobalOptIn,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		dynamicSurfaceConfigItems: EditorPluginAIConfigItemWithOptions[];
		intl: IntlShape;
	}) =>
	(view: EditorView) => {
		const parentConfigItems = dynamicSurfaceConfigItems.reduce(
			(accumulator, configItem) => {
				const nestingConfig = configItem.config.nestingConfig;
				if (nestingConfig) {
					if (!accumulator[nestingConfig.id]) {
						accumulator[nestingConfig.id] = [] as EditorPluginAIConfigItemWithOptions[];
					}
					accumulator[nestingConfig.id].push(configItem);
				}
				return accumulator;
			},
			{} as Record<string, EditorPluginAIConfigItemWithOptions[]>,
		);
		const processedParentConfigItem = [] as string[];

		const actions: BaseAIAction[] = [];

		dynamicSurfaceConfigItems.forEach(({ config }) => {
			if (config.nestingConfig) {
				if (!processedParentConfigItem.includes(config.nestingConfig.id)) {
					processedParentConfigItem.push(config.nestingConfig.id);

					const childrenConfigItems = parentConfigItems[config.nestingConfig.id];
					const childrenActions = childrenConfigItems.map((configItem) =>
						convertConfigItemToAction({ config: configItem.config, aiGlobalOptIn, intl, view }),
					);

					actions.push({
						id: config.nestingConfig.id,
						title: intl.formatMessage(config.nestingConfig.parentTitle),
						description: intl.formatMessage(config.nestingConfig.parentTitle),
						icon: config.nestingConfig.icon?.({ formatMessage: intl.formatMessage }) || (
							<AtlassianIntelligenceIcon label="" spacing="spacious" />
						),
						children: childrenActions,
						status: config.statusLozengeType,
					});
				}
			} else {
				actions.push(convertConfigItemToAction({ config, aiGlobalOptIn, intl, view }));
			}
		});

		return actions;
	};

/**
 * Give separate list of config items for;
 * - which can run only on empty document
 * - which can run only on non empty document
 * - which can run only on selection
 */
export function getConfigItemsListForSelection(
	dynamicSurfaceConfigItems: EditorPluginAIConfigItemWithOptions[],
) {
	/**
	 * Empty document config items are for prompts:
	 * - which can run when document is empty. Currently we have only one empty prompt - Brainstorm.
	 */
	const emptyDocConfigItems = dynamicSurfaceConfigItems.filter((configWithOptions) => {
		const emptyTrigger = configWithOptions.triggers.empty;
		return emptyTrigger && emptyTrigger.enabled && emptyTrigger.docMinSize === 0;
	});
	/**
	 * Non empty document, but selection without content config items are for prompts:
	 * - which can run only on non-empty documents (e.g improve writing, change tone etc)
	 */
	const nonEmptyDocConfigItems = dynamicSurfaceConfigItems.filter((configWithOptions) => {
		const emptyTrigger = configWithOptions.triggers.empty;
		return emptyTrigger.enabled && (!emptyTrigger.docMinSize || emptyTrigger.docMinSize >= 0);
	});

	/**
	 * Selection with content config items are for prompts:
	 * - which can run only on selection (e.g Convert to table, convert to bullet list etc)
	 */
	const selectionConfigItems = dynamicSurfaceConfigItems.filter((configWithOptions) => {
		const selectionTrigger = configWithOptions.triggers.selection;
		return (
			selectionTrigger.enabled &&
			(!selectionTrigger.selectionMinSize || selectionTrigger.selectionMinSize >= 0)
		);
	});

	return { emptyDocConfigItems, nonEmptyDocConfigItems, selectionConfigItems };
}

export function getActionsListForSelection({
	intl,
	dynamicSurfaceConfigItems,
	aiGlobalOptIn,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	dynamicSurfaceConfigItems: EditorPluginAIConfigItemWithOptions[];
	intl: IntlShape;
	view: EditorView;
}) {
	const { emptyDocConfigItems, nonEmptyDocConfigItems, selectionConfigItems } =
		getConfigItemsListForSelection(dynamicSurfaceConfigItems);
	const emptyDocActions = mapConfigItemsToActions({
		aiGlobalOptIn,
		dynamicSurfaceConfigItems: emptyDocConfigItems,
		intl,
	})(view);
	const nonEmptyDocActions = mapConfigItemsToActions({
		aiGlobalOptIn,
		dynamicSurfaceConfigItems: nonEmptyDocConfigItems,
		intl,
	})(view);
	const selectionActions = mapConfigItemsToActions({
		aiGlobalOptIn,
		dynamicSurfaceConfigItems: selectionConfigItems,
		intl,
	})(view);

	return { emptyDocActions, nonEmptyDocActions, selectionActions };
}

export function chooseActionsToShowForSelection(
	{ isEmptyDoc, isEmptySelection }: { isEmptyDoc: boolean; isEmptySelection: boolean },
	{
		emptyDocActions,
		nonEmptyDocActions,
		selectionActions,
	}: {
		emptyDocActions: BaseAIAction[];
		nonEmptyDocActions: BaseAIAction[];
		selectionActions: BaseAIAction[];
	},
) {
	if (isEmptyDoc) {
		return emptyDocActions;
	} else if (isEmptySelection) {
		return nonEmptyDocActions;
	} else {
		return selectionActions;
	}
}
